/* MajetekTab — nefinanční majetek klienta ve složce.
 *
 * ZADÁNÍ VLASTNÍKA (7. 8. 2026): „Já když teď vyplním, že klient má byt za
 * 8 milionů, tak se nikde neukazuje, že má 8 milionů." Analýza propisovala do
 * složky jen smlouvy; byt, auto, pole, lesy a zlato zůstávaly zavřené uvnitř
 * analýzy.
 *
 * PROČ NE V ZÁLOŽCE SMLOUVY: byt není smlouva. Kdyby seděl mezi smlouvami,
 * složka by u něj nabízela „Výpověď do pojišťovny" a majetek by se počítal
 * mezi finanční produkty. Proto vlastní záložka a vlastní kolekce.
 *
 * NEZNÁMÉ ≠ NULA: položka bez ocenění se NIKDY nekreslí jako 0 Kč. Ukáže se
 * jako „cena neuvedena" i s důvodem, a do součtu nevstupuje. Součet proto
 * nese poznámku, kolik položek do něj není započítaných — jinak by poradce
 * četl neúplné číslo jako celkový majetek klienta.
 */
import { motion } from "framer-motion";
import { Home, Car, Trees, Gem, Building2, Gift, Package, Landmark, ShieldCheck } from "lucide-react";
import { nefinLabel, druhLabel } from "@/lib/analyzaV2/nefinancni";
/* Paleta druhů je sdílená s filtrem v seznamu klientů — kdyby si každý držel
 * vlastní, chip ve filtru by měl jinou barvu než karta tady. */
import { barvaDruhu } from "@/lib/crm/majetekFiltr";
import { TLj } from "@/lib/prekladyUI";
import { menaTenanta } from "@/lib/tenantMena";
import { sJednotkou } from "@/lib/castka";

const IKONA = {
  nemovitost: Home,
  pozemek: Trees,
  vozidlo: Car,
  cennosti: Gem,
  firma: Building2,
  dedictvi: Gift,
  ostatni: Package,
};

const cislo = (n) => new Intl.NumberFormat("cs-CZ").format(Math.round(Number(n) || 0));

/* Hodnoty majetku se ukládají v DOMÁCÍ MĚNĚ TENANTA (CZ → CZK, SK → EUR), ne vždy
 * v CZK (oprava CRM-SK-11, 18. 10. 2026). Formát dělá složka jedním sdíleným
 * fmtMoney — nesmí se počítat tady zvlášť, jinak by karta majetku ukázala jiné
 * číslo než KPI na Kartě klienta.
 * Záložní formát je jen pro testy a pro případ, že prop nedorazí. */
const zalozniFmt = (n) => sJednotkou(cislo(n), menaTenanta().symbol);

/* Má položka cenu?
 *
 * POZOR na Number(null) === 0 — na tohle se dá naletět a je to přesně ta
 * chyba, kterou celá záložka řeší: položka s vymazanou cenou (backend vrátí
 * null) by prošla přes Number.isFinite a vykreslila se jako 0 Kč, tedy jako
 * tvrzení, že majetek nemá žádnou hodnotu. Prázdno musí zůstat prázdnem.
 */
const maCenu = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

export default function MajetekTab({ majetek, fmtMoney = zalozniFmt, lang = "cs", chyba, onRetry }) {
  const polozky = Array.isArray(majetek) ? majetek.filter(Boolean) : [];

  if (chyba) {
    return (
      <div className="text-center py-10" data-testid="majetek-error">
        <div className="text-[13px] font-semibold" style={{ color: "var(--negative-text)" }}>
          {TLj("Majetek se nepodařilo načíst", lang)}
        </div>
        {/* Prázdná obrazovka po chybě by poradce četl jako „klient nic nemá"
            a řekl by to klientovi nahlas. */}
        <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>
          {lang === "sk"
            ? "Neznamená to, že klient majetok nemá — dáta sa nenačítali."
            : "Neznamená to, že klient majetek nemá — data se nenačetla."}
        </div>
        {onRetry && (
          <button onClick={onRetry} data-testid="majetek-retry"
            className="mt-3 px-4 py-2 rounded-full text-[12px] font-semibold"
            style={{ background: "var(--ink, #26221A)", color: "var(--bg, #F7F4EC)" }}>
            {TLj("Zkusit znovu", lang)}
          </button>
        )}
      </div>
    );
  }

  if (!polozky.length) {
    return (
      <div
        className="rounded-2xl p-12 text-center"
        style={{ background: "var(--surface)", outline: "1px dashed var(--line)", outlineOffset: "-1px" }}
        data-testid="majetek-empty"
      >
        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center"
          style={{ background: "var(--accent-soft)", outline: "1px solid var(--accent-soft)", outlineOffset: "-1px" }}>
          <Landmark size={20} style={{ color: "var(--accent)" }} />
        </div>
        <div className="font-display text-[15px] font-semibold" style={{ color: "var(--ink)" }}>
          {TLj("Žádný evidovaný majetek", lang)}
        </div>
        <div className="text-[12px] mt-1.5" style={{ color: "var(--muted)" }}>
          {lang === "sk"
            ? "Nehnuteľnosti, autá, pôdu, cennosti a podiely vyplň v analýze — do zložky sa prepíšu samy."
            : "Nemovitosti, auta, půdu, cennosti a podíly vyplň v analýze — do složky se propíšou samy."}
        </div>
      </div>
    );
  }

  const ocenene = polozky.filter((p) => maCenu(p.value));
  const chybiCena = polozky.length - ocenene.length;
  const celkem = ocenene.reduce((a, p) => a + (Number(p.value) || 0), 0);
  const prijem = polozky.reduce((a, p) => a + (Number(p.monthly_income) || 0), 0);

  return (
    <div data-testid="majetek-list">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--muted)" }}>
          {polozky.length} {lang === "sk" ? "položiek majetku" : pocetLabel(polozky.length)}
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="text-right">
            <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>
              {TLj("Hodnota celkem", lang)}
            </div>
            <div className="font-display text-[18px] font-semibold mono tabular-nums" style={{ color: "var(--ink)" }}
              data-testid="majetek-celkem">
              {fmtMoney(celkem)}
            </div>
            {/* Neúplný součet se musí přiznat — poradce jinak přečte číslo
                jako celkový majetek klienta a řekne ho nahlas. */}
            {chybiCena > 0 && (
              <div className="text-drobny mt-0.5" style={{ color: "var(--warn-text)" }} data-testid="majetek-neuplny-soucet">
                {lang === "sk"
                  ? `bez ${chybiCena} položiek bez ceny`
                  : `bez ${chybiCena} ${chybiCena === 1 ? "položky" : "položek"} bez ceny`}
              </div>
            )}
          </div>
          {prijem > 0 && (
            <div className="text-right">
              <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>
                {TLj("Příjem měsíčně", lang)}
              </div>
              <div className="font-display text-[18px] font-semibold mono tabular-nums" style={{ color: "var(--positive-text)" }}
                data-testid="majetek-prijem">
                {fmtMoney(prijem)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {polozky.map((p, i) => <MajetekCard key={p.id || `${p.type}-${i}`} p={p} i={i} fmtMoney={fmtMoney} lang={lang} />)}
      </div>
    </div>
  );
}

/* České skloňování počtu — 1 položka / 2–4 položky / 5+ položek. */
function pocetLabel(n) {
  if (n === 1) return "položka majetku";
  if (n < 5) return "položky majetku";
  return "položek majetku";
}

function MajetekCard({ p, i, fmtMoney, lang }) {
  const druh = p.kind || "ostatni";
  const barva = barvaDruhu(druh);
  const Ikona = IKONA[druh] || Package;
  const cena = maCenu(p.value);
  const zAnalyzy = p.source === "analyza";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.04, type: "spring", stiffness: 360, damping: 28 }}
      className="elite-card relative overflow-hidden p-4"
      data-testid={`majetek-card-${p.id || i}`}
    >
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5"
        style={{ background: `linear-gradient(180deg, ${barva} 0%, ${barva}99 100%)` }} />
      <div className="flex items-start gap-3 pl-2">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${barva}14`, outline: `1px solid ${barva}33`, outlineOffset: "-1px" }}>
          <Ikona size={18} style={{ color: barva }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-[15px] font-semibold tracking-tight" style={{ color: "var(--ink)" }}>
              {p.name}
            </span>
            <span className="text-popisek uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
              style={{ background: `${barva}14`, color: barva, outline: `1px solid ${barva}33`, outlineOffset: "-1px" }}
              data-testid={`majetek-druh-${p.id || i}`}>
              {druhLabel(druh, lang) || druh}
            </span>
            {zAnalyzy && (
              <span className="text-drobny px-1.5 py-0.5 rounded font-semibold"
                style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                title={TLj("Přeneseno z finanční analýzy", lang)}>
                {TLj("z analýzy", lang)}
              </span>
            )}
          </div>

          {/* Přesný typ pod názvem — „Nemovitost k pronájmu" vs. „Nemovitost k bydlení". */}
          {nefinLabel(p.type, lang) && (
            <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }}>{nefinLabel(p.type, lang)}</div>
          )}
          {p.address && (
            <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }}>{p.address}</div>
          )}

          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-drobny">
            <div>
              <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>
                {TLj("Hodnota", lang)}
              </div>
              {cena ? (
                <div className="font-display text-[15px] font-semibold mono tabular-nums" style={{ color: barva }}
                  data-testid={`majetek-hodnota-${p.id || i}`}>
                  {fmtMoney(p.value)}
                </div>
              ) : (
                /* NIKDY nula. Nula by tvrdila, že majetek nemá cenu. */
                <div className="text-[12px] italic" style={{ color: "var(--warn-text)" }}
                  data-testid={`majetek-bez-ceny-${p.id || i}`}>
                  {TLj("cena neuvedena", lang)}
                  {p.value_unknown_reason ? ` — ${p.value_unknown_reason}` : ""}
                </div>
              )}
            </div>

            {Number(p.monthly_income) > 0 && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>
                  {TLj("Příjem měsíčně", lang)}
                </div>
                <div className="font-semibold mono tabular-nums" style={{ color: "var(--ink)" }}>
                  {fmtMoney(p.monthly_income)}
                </div>
              </div>
            )}

            {/* Cizí měna: původní částka zůstává vidět, ať poradce ví, z čeho se počítalo.
                „Cizí" = jiná než měna tenanta (dřív literál „CZK" → SK poradce viděl
                „Pôvodná suma 210 000 EUR" u vlastní domácí měny). */}
            {p.original_amount > 0 && p.currency && String(p.currency).toUpperCase() !== menaTenanta().code && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>
                  {TLj("Původní částka", lang)}
                </div>
                <div className="font-semibold mono tabular-nums" style={{ color: "var(--muted)" }}>
                  {cislo(p.original_amount)} {p.currency}
                </div>
              </div>
            )}
          </div>

          {p.insured === true && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-drobny font-semibold"
              style={{ color: "var(--positive-text)" }}>
              <ShieldCheck size={12} /> {TLj("Pojištěno", lang)}
            </div>
          )}
          {p.notes && (
            <div className="text-drobny mt-2" style={{ color: "var(--muted)" }}>{p.notes}</div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
